import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';
import { SignInGate } from '../SignIn.jsx';
import { checkStaff } from './moderation.js';
import {
  approveCheck, approveErrorMessage, fetchEmailUsage, fileUrls, formatDate, formatWhen, getCheck, idTypeLabels, listRecentChecks, listWaitingChecks,
  rejectCheck, rejectionReasonLabels, rejectionReasons, routeLabels, slotLabel, validateApproval, waitingFor,
} from './staffAdmin.js';
import './moderation.css';
import './admin.css';
import './admin-records.css';
import { count, showTrades } from './adminData.js';
import AutomaticEmails from './AutomaticEmails.jsx';
import HousingEnquiries from './HousingEnquiries.jsx';
import OverviewTab from './OverviewTab.jsx';
import PeopleTab from './PeopleTab.jsx';
import CouncilsTab from './CouncilsTab.jsx';
import TradesTab from './TradesTab.jsx';
import JobsTab from './JobsTab.jsx';
import { OUTREACH_REFRESH_EVERY, lastOutreach, loadOutreach, onOutreach, outreachLoadedAt } from './outreachData.js';
import ProviderOutreach from './ProviderOutreach.jsx';
import DataTab from './DataTab.jsx';
import RoadmapTab from './RoadmapTab.jsx';
import RevenueTab from './RevenueTab.jsx';
import ExpensesTab from './ExpensesTab.jsx';
import NetworkTab from './NetworkTab.jsx';
import CampaignEmails from './CampaignEmails.jsx';
import ProviderProspects from './ProviderProspects.jsx';
import { FundingTab, InvestorsTab } from './ResearchTabs.jsx';
import TikTokTab from './TikTokTab.jsx';
import { isNewReply, onSeenChange, readSeen, startSeen } from './repliesSeen.js';
import { SortableTable } from './SortableTable.jsx';
import { labelOf } from './sorting.js';

// Admins get the overview screens first; moderators only the daily staff work. The database refuses every call a tab is not allowed to make.
// The Trades tab is behind `showTrades` (adminData.js): while it is false the tab is not listed, and `?tab=trades` falls back to the first tab.
const adminTabs = [
  ['overview', 'Overview'], ['roadmap', 'Roadmap'], ['people', 'Users'], ['providers', 'Housing providers'], ['referrals', 'Referrals'], ['councils', 'Councils'], ...(showTrades ? [['trades', 'Trades']] : []),
  ['jobs', 'Jobs'], ['ids', 'ID checks'], ['inbox', 'Inbox'], ['outreach', 'Outreach'], ['sponsorship', 'Sponsorship'], ['investors', 'Investors'], ['funding', 'Funding'], ['revenue', 'Revenue'], ['expenses', 'Expenses'], ['influence', 'Influence'], ['network', 'Network'],
  ['data', 'Data'], ['labels', 'Labels'],
];
// Outreach, Sponsorship, Investors, Funding, Revenue and Expenses are email, money and research that only admins can see.
const staffTabs = [['ids', 'ID checks'], ['inbox', 'Inbox']];
// What comes in for staff to deal with, picked with `?show=`. TikTok shows sample comments until TikTok approves our app (docs/tiktok-comments-api.md).
const inboxViews = [['housing', 'Housing enquiries'], ['tiktok', 'TikTok']];
// Tabs that were merged, moved or renamed, so old links still land in the right place.
const movedTabs = {
  partners: { tab: 'providers' },
  campaigns: { tab: 'outreach' },
  emails: { tab: 'outreach' },
  'id-checks': { tab: 'ids' },
  housing: { tab: 'inbox', show: 'housing' },
  tiktok: { tab: 'inbox', show: 'tiktok' },
};

/** Staff dashboard for the laptop: the admin overview, ID checks, the staff inbox and outreach. The database refuses every call from non-staff. */
export function AdminDashboard() {
  const { user, loading } = useAuth();
  const [access, setAccess] = useState(null);
  useEffect(() => {
    let live = true;
    if (!user) { setAccess(null); return undefined; }
    checkStaff(supabase).then((value) => { if (live) setAccess(value); });
    return () => { live = false; };
  }, [user]);

  if (loading) return <div className="content-wrap">Loading account…</div>;
  if (!user) return <SignInGate next="/admin"/>;
  if (!access) return <div className="content-wrap">Checking access…</div>;
  if (!access.staff) {
    return (
      <div className="content-wrap account-page">
        <h1>You don't have access</h1>
        <p>This area is for Resident Service 360 staff only.</p>
        <Link to="/account">Back to your account</Link>
      </div>
    );
  }
  return <Dashboard role={access.role}/>;
}

export default AdminDashboard;

/**
 * How many housing providers have replied to naima@ since their last reply was opened in this browser: the number on the Outreach
 * tab. It counts from Naima's emails as Overview or Outreach last read them, and while the page is in front it reads them again
 * once they are 10 minutes old (checked every minute), so the number moves on whichever tab is open. Admins only.
 */
function useUnreadReplies(enabled) {
  const [outreach, setOutreach] = useState(lastOutreach);
  const [seen, setSeen] = useState(readSeen);
  useEffect(() => (enabled ? onOutreach(setOutreach) : undefined), [enabled]);
  useEffect(() => (enabled ? onSeenChange(() => setSeen(readSeen())) : undefined), [enabled]);
  useEffect(() => {
    if (!enabled) return undefined;
    let triedAt = 0;
    const check = () => {
      const stale = Date.now() - Math.max(outreachLoadedAt(), triedAt) >= OUTREACH_REFRESH_EVERY;
      if (document.hidden || !stale) return;
      triedAt = Date.now();
      loadOutreach(supabase).catch(() => { /* tried again in 10 minutes; Outreach shows the error when opened */ });
    };
    const timer = window.setInterval(check, 60 * 1000);
    return () => window.clearInterval(timer);
  }, [enabled]);
  useEffect(() => {
    if (enabled && outreach && !seen) setSeen(startSeen(outreach.rows));
  }, [enabled, outreach, seen]);
  return enabled && outreach ? outreach.rows.filter((row) => isNewReply(row, seen)).length : 0;
}

// Who is signed in, and Sign out, live in the site header's Your account menu.
function Dashboard({ role }) {
  const [params, setParams] = useSearchParams();
  // New replies not opened yet, by the tab they are on. Sponsorship, Investors and Referrals join once their email is read here.
  const unread = { outreach: useUnreadReplies(role === 'admin') };
  const tabs = role === 'admin' ? adminTabs : staffTabs;
  // ID checks used to be in the Inbox, so an Inbox link that asks for them opens their own tab.
  const moved = params.get('tab') === 'inbox' && params.get('show') === 'id-checks' ? { tab: 'ids' } : movedTabs[params.get('tab')] || {};
  const asked = moved.tab || params.get('tab');
  const tab = tabs.some(([key]) => key === asked) ? asked : tabs[0][0];
  // Opening anything replaces the whole query, so the person or company being viewed never leaks into another tab.
  const open = (next) => setParams(next);
  return (
    <div className="content-wrap account-page admin-page">
      <h1 className="visually-hidden">Staff dashboard</h1>
      <div className="admin-bar">
        <div className="segmented mod-tabs" role="tablist" aria-label="Dashboard sections">
          {tabs.map(([key, label]) => (
            <button key={key} role="tab" aria-selected={tab === key} className={tab === key ? 'active' : ''} onClick={() => setParams({ tab: key })}>
              {label}
              {unread[key] > 0 && (
                <>
                  <span className="admin-tab-count" aria-hidden="true">{unread[key]}</span>
                  <span className="visually-hidden">{`, ${unread[key]} new`}</span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>
      {tab === 'overview' ? <OverviewTab onOpen={open}/>
        : tab === 'people' ? <PeopleTab type={params.get('type') || 'all'} personId={params.get('person') || ''} onOpen={open}/>
        : tab === 'providers' ? <ProviderProspects onOpen={open} heading={false}/>
        : tab === 'councils' ? <CouncilsTab councilCode={params.get('council') || ''} onOpen={open}/>
        : tab === 'trades' && showTrades ? <TradesTab tradeId={params.get('trade') || ''} onOpen={open}/>
        : tab === 'jobs' ? <JobsTab onOpen={open}/>
        : tab === 'ids' ? <IdChecksTab/>
        : tab === 'outreach' ? <><ProviderOutreach/><AutomaticEmails/></>
        : tab === 'roadmap' ? <RoadmapTab/>
        : tab === 'referrals' ? <ReferralsTab/>
        : tab === 'sponsorship' ? <CampaignEmails campaign="sponsorship" name="Sponsorship"/>
        : tab === 'investors' ? <InvestorsTab/>
        : tab === 'funding' ? <FundingTab/>
        : tab === 'revenue' ? <RevenueTab module={params.get('module')} payee={params.get('payee')} onOpen={(next) => setParams({ tab: 'revenue', ...next })}/>
        : tab === 'expenses' ? <ExpensesTab/>
        : tab === 'influence' ? <p className="admin-empty">Nothing on Influence yet.</p>
        : tab === 'network' ? <NetworkTab show={params.get('show')} onShow={(show) => setParams({ tab: 'network', show })}/>
        : tab === 'data' ? <DataTab show={params.get('show')} category={params.get('category')} record={params.get('record')} onOpen={(next) => setParams({ tab: 'data', ...next })}/>
        : tab === 'labels' ? <LabelsTab/>
        : <InboxTab show={moved.show || params.get('show')} onShow={(show) => setParams({ tab: 'inbox', show })}/>}
    </div>
  );
}

/** Referrals with our partners: the ones they have sent us, and the ones we have sent them. Nothing records them yet. */
function ReferralsTab() {
  return (
    <>
      <h2 className="admin-section">Received from partners</h2>
      <p className="admin-empty">No referrals received yet.</p>
      <h2 className="admin-section">Sent to partners</h2>
      <p className="admin-empty">No referrals sent yet.</p>
    </>
  );
}

// Every address we send email from, or plan to: the person or job it is, what it is for, and where its incoming mail goes. Mail to
// hello@, ryan@, naima@ and team@ is forwarded to the owner's inbox by netlify/functions/inbound-email.mjs (INBOUND_MAILBOXES), sent on
// from inbox@. A planned address can send as soon as it is used, but its replies go nowhere until it joins that list.
const RS360 = 'residentservice360.bedmaps.com';
const FORWARDED = 'Forwarded to your inbox';
const NOT_YET = 'Not set up yet';
const addressGroups = [
  {
    brand: 'Resident Service 360',
    rows: [
      { address: `cumar@${RS360}`, title: 'Director', purpose: 'Investors, key partners and anything that needs the owner.', incoming: NOT_YET, status: 'Planned' },
      { address: `naima@${RS360}`, title: 'Housing Partnerships Manager', purpose: 'Housing provider outreach: emails providers about taking referrals, and gets their replies.', incoming: FORWARDED, status: 'In use' },
      { address: `ryan@${RS360}`, title: 'Resident Services Manager', purpose: "The site's automatic emails: ID checks waiting, room checks and applications, invitations, notifications and repair reports.", incoming: FORWARDED, status: 'In use' },
      { address: `priya@${RS360}`, title: 'Council Partnerships Manager', purpose: 'Talks to council housing teams.', incoming: NOT_YET, status: 'Planned' },
      { address: `amelia@${RS360}`, title: 'Supplier Relations Manager', purpose: 'Invites local trades and looks after them, so housing provider outreach stays with Naima.', incoming: NOT_YET, status: 'Planned' },
      { address: `amara@${RS360}`, title: 'Referrals Manager', purpose: 'Sends referrals to partners and handles the ones they send us.', incoming: NOT_YET, status: 'Planned' },
      { address: `oliver@${RS360}`, title: 'Sponsorship Manager', purpose: 'Finds and looks after sponsors.', incoming: NOT_YET, status: 'Planned' },
      { address: `hannah@${RS360}`, title: 'Investor Relations Manager', purpose: 'Investor outreach and updates.', incoming: NOT_YET, status: 'Planned' },
      { address: `tom@${RS360}`, title: 'Funding Manager', purpose: 'Grants and loans, such as government-backed start-up loans.', incoming: NOT_YET, status: 'Planned' },
      { address: `zainab@${RS360}`, title: 'Social Media Manager', purpose: 'Replies to TikTok comments.', incoming: NOT_YET, status: 'Planned' },
      { address: `hello@${RS360}`, title: 'Customer Experience Manager', purpose: 'Sign-up emails: confirming an email address and resetting a password.', incoming: FORWARDED, status: 'In use' },
      { address: `team@${RS360}`, title: 'Support Manager', purpose: 'The staff team inbox: staff alerts, and the contact address on the housing enquiry privacy notice.', incoming: FORWARDED, status: 'In use' },
      { address: `inbox@${RS360}`, title: 'Office Manager', purpose: 'Passes on every email that comes in to the forwarded addresses, to your Aston Metrics inbox.', incoming: 'Not received', status: 'In use' },
    ],
  },
  {
    brand: 'BedMaps',
    rows: [
      { address: 'cumar@bedmaps.com', title: 'Director', purpose: 'Investors, key partners and anything that needs the owner.', incoming: NOT_YET, status: 'Planned' },
      { address: 'charlotte@bedmaps.com', title: 'Provider Network Manager', purpose: 'Grows and looks after the housing provider network.', incoming: NOT_YET, status: 'Planned' },
      { address: 'cameron@bedmaps.com', title: 'Local Authority Partnerships Manager', purpose: 'Works with councils on placements.', incoming: NOT_YET, status: 'Planned' },
      { address: 'tariq@bedmaps.com', title: 'Placements Coordinator', purpose: 'Referrals and moves between areas.', incoming: NOT_YET, status: 'Planned' },
      { address: 'george@bedmaps.com', title: 'Property Listings Manager', purpose: '"List a Property" submissions.', incoming: NOT_YET, status: 'Planned' },
      { address: 'emily@bedmaps.com', title: 'Customer Experience Manager', purpose: 'Support for housing providers and councils.', incoming: NOT_YET, status: 'Planned' },
      { address: 'notifications@bedmaps.com', title: 'Systems Manager', purpose: "BedMaps' notifications sender, from before Ryan took over the site's automatic emails.", incoming: 'Not received', status: 'In use' },
    ],
  },
];

const addressColumns = [
  { key: 'address', label: 'Address', value: (row) => row.address, cell: (row) => row.address },
  { key: 'title', label: 'Title', value: (row) => row.title, cell: (row) => row.title },
  { key: 'purpose', label: 'Purpose', value: (row) => row.purpose, cell: (row) => row.purpose },
  { key: 'incoming', label: 'Incoming mail', value: (row) => row.incoming, cell: (row) => row.incoming },
  {
    key: 'status',
    label: 'Status',
    value: (row) => row.status,
    cell: (row) => <span className={`admin-status is-${row.status === 'In use' ? 'good' : 'neutral'}`}>{row.status}</span>,
  },
];

/** How much of a limit is used: the number against the limit, and a thin bar that turns amber past 70% and red past 90%. */
function Meter({ label, used, limit, more = false }) {
  const share = Math.min(1, used / limit);
  const tone = share >= 0.9 ? 'bad' : share >= 0.7 ? 'warn' : 'ok';
  return (
    <div className={`usage-meter is-${tone}`}>
      <p className="usage-figure">
        <strong>{`${count(used)}${more ? '+' : ''}`}</strong>
        {` / ${count(limit)} `}
        <span>{label}</span>
      </p>
      <span className="usage-track" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used}>
        <span className="usage-fill" style={{ width: `${share * 100}%` }}/>
      </span>
    </div>
  );
}

/** Resend's free plan, used against its limits: emails today (a UTC day) and this month, sent and received on both domains, and domains. */
function ResendUsage() {
  const [state, setState] = useState({ usage: null, error: '' });
  useEffect(() => {
    let live = true;
    fetchEmailUsage(supabase).then(
      (usage) => { if (live) setState({ usage, error: '' }); },
      (cause) => { if (live) setState({ usage: null, error: cause.message }); },
    );
    return () => { live = false; };
  }, []);
  if (state.error) return <p className="mod-error" role="alert">{state.error}</p>;
  if (!state.usage) return <p className="mod-note">Loading Resend usage…</p>;
  const { today, month, domains, limits, complete } = state.usage;
  return (
    <div className="usage" aria-label="Resend usage">
      <Meter label="emails today" used={today} limit={limits.day} more={!complete}/>
      <Meter label="emails this month" used={month} limit={limits.month} more={!complete}/>
      <Meter label="domains" used={domains} limit={limits.domains}/>
    </div>
  );
}

/** The email addresses we use, and the ones planned, by brand: who each is and what it does, under how much of Resend is used. */
function LabelsTab() {
  return [<ResendUsage key="usage"/>, ...addressGroups.map((group) => {
    const id = `labels-${group.brand.toLowerCase().replace(/\W+/g, '-')}`;
    return (
      <section key={group.brand} aria-labelledby={id}>
        <h2 className="admin-section" id={id}>{group.brand}</h2>
        <SortableTable columns={addressColumns} rows={group.rows} rowKey={(row) => row.address}/>
      </section>
    );
  })];
}

/** Housing enquiries and TikTok comments in one place, one at a time. */
function InboxTab({ show, onShow }) {
  const view = inboxViews.some(([key]) => key === show) ? show : inboxViews[0][0];
  return (
    <>
      <div className="mod-filters">
        <label>Show
          <select value={view} onChange={(event) => onShow(event.target.value)}>
            {inboxViews.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
      </div>
      {view === 'tiktok' ? <TikTokTab/> : <HousingEnquiries/>}
    </>
  );
}

const idTypeName = (type) => idTypeLabels[type] || type;
const decisionText = (row) => (row.status === 'approved' ? 'Approved' : `Rejected: ${rejectionReasonLabels[row.rejection_reason] || row.rejection_reason}`);

const decisionColumns = [
  { key: 'resident', label: 'Resident', value: (row) => row.display_name, cell: (row) => row.display_name },
  { key: 'id', label: 'ID', value: (row) => labelOf(row.id_type, idTypeName), cell: (row) => idTypeName(row.id_type) },
  {
    key: 'decision',
    label: 'Decision',
    value: decisionText,
    cell: (row) => (row.status === 'approved'
      ? <span className="admin-status is-good">Approved</span>
      : <span className="admin-status is-bad">{decisionText(row)}</span>),
  },
  { key: 'decided', label: 'Decided', value: (row) => row.reviewed_at, first: 'desc', cell: (row) => formatWhen(row.reviewed_at) },
  { key: 'by', label: 'By', value: (row) => row.reviewed_by_name, cell: (row) => row.reviewed_by_name || '–' },
];

function IdChecksTab() {
  const [state, setState] = useState({ waiting: null, recent: [], error: '' });
  const [selectedId, setSelectedId] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    try {
      const [waiting, recent] = await Promise.all([listWaitingChecks(supabase), listRecentChecks(supabase)]);
      setState({ waiting, recent, error: '' });
    } catch (cause) {
      setState({ waiting: [], recent: [], error: cause.message });
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!state.waiting) return <p>Loading ID checks…</p>;
  const waiting = state.waiting;
  const selected = waiting.find((row) => row.id === selectedId) || waiting[0];
  const done = (message) => { setNotice(message); setSelectedId(''); void load(); };

  return (
    <>
      {state.error && <p className="mod-error" role="alert">{state.error}</p>}
      {notice && <p className="admin-notice" role="status">{notice}</p>}
      {waiting.length === 0
        ? <p className="admin-empty">No ID checks waiting.</p>
        : (
          <div className="mod-layout">
            <ul className="mod-queue" aria-label="ID checks waiting">
              {waiting.map((row) => (
                <li key={row.id}>
                  <button type="button" className={row.id === selected?.id ? 'selected' : ''} onClick={() => { setSelectedId(row.id); setNotice(''); }}>
                    <strong>{row.display_name || 'A resident'}</strong>
                    <span>{idTypeLabels[row.id_type] || row.id_type}</span>
                    <small>Waiting {waitingFor(row.submitted_at)} · sent {formatWhen(row.submitted_at)}</small>
                  </button>
                </li>
              ))}
            </ul>
            {selected && <CheckReview key={selected.id} id={selected.id} onDone={done}/>}
          </div>
        )}
      <h2 className="admin-section">Recent decisions</h2>
      {state.recent.length === 0
        ? <p className="admin-empty">No checks have been decided yet.</p>
        : (
          <SortableTable columns={decisionColumns} rows={state.recent} rowKey={(row) => row.id}/>
        )}
    </>
  );
}

function CheckReview({ id, onDone }) {
  const [check, setCheck] = useState(null);
  const [urls, setUrls] = useState({});
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ idNumber: '', nameOnId: '', dobOnId: '', reason: '', note: '' });
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const value = await getCheck(supabase, id);
        const links = await fileUrls(supabase, value.files);
        if (live) { setCheck(value); setUrls(links); setForm((current) => ({ ...current, dobOnId: value.entered_dob || '' })); }
      } catch (cause) {
        if (live) setLoadError(cause.message);
      }
    })();
    return () => { live = false; };
  }, [id]);

  if (loadError) return <div className="mod-detail"><p className="mod-error" role="alert">{loadError}</p></div>;
  if (!check) return <div className="mod-detail"><p>Loading this check…</p></div>;

  const name = check.display_name || 'This resident';
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  async function approve(event) {
    event.preventDefault();
    const problem = validateApproval(form);
    if (problem) { setError(problem); return; }
    setBusy('approve'); setError('');
    try {
      await approveCheck(supabase, { id: check.id, ...form });
      onDone(`${name} is approved. They've been told in the app, and any applications they made are on their way to the providers.`);
    } catch (cause) { setError(approveErrorMessage(cause)); setBusy(''); }
  }

  async function reject(event) {
    event.preventDefault();
    if (!form.reason) { setError('Choose a reason for rejecting.'); return; }
    setBusy('reject'); setError('');
    try {
      await rejectCheck(supabase, { id: check.id, reason: form.reason, note: form.note });
      onDone(`${name}'s check is rejected. They've been told why and can send a new one.`);
    } catch (cause) { setError(approveErrorMessage(cause)); setBusy(''); }
  }

  return (
    <section className="mod-detail" aria-label={`ID check for ${name}`}>
      <h2>{name}</h2>
      <p className="mod-note">{routeLabels[check.route] || check.route} · {idTypeLabels[check.id_type] || check.id_type}</p>
      {check.lookalikes?.length > 0 && (
        <p className="mod-flag">A checked account has the same name and date of birth ({check.lookalikes.map((person) => person.display_name).join(', ')}). Make sure this isn't the same person.</p>
      )}
      <dl className="mod-fields">
        <dt>Action asked</dt><dd>{check.action_prompt}</dd>
        <dt>Date of birth entered</dt><dd>{formatDate(check.entered_dob)}</dd>
        <dt>Sent</dt><dd>{formatWhen(check.submitted_at)}</dd>
      </dl>
      {check.files.length === 0 && <p className="mod-note">The files for this check have been deleted.</p>}
      <div className="admin-files">
        {check.files.map((file) => {
          const label = slotLabel(check.id_type, file.slot);
          const url = urls[file.slot];
          const isDocument = check.route === 'documents' && file.slot !== 'selfie';
          return (
            <figure key={file.slot} className="admin-file">
              {url
                ? (isDocument
                  ? <a className="admin-document" href={url} target="_blank" rel="noreferrer">Open the document (PDF or photo)</a>
                  : <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={label}/></a>)
                : <p className="mod-note">This file couldn't be loaded. Open the check again.</p>}
              <figcaption>{label}{url && !isDocument ? ' · click to open full size' : ''}</figcaption>
            </figure>
          );
        })}
      </div>
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="admin-decide">
        <form className="mod-form" onSubmit={approve}>
          <strong>Approve</strong>
          <label>{check.route === 'documents' ? 'National Insurance number' : 'Document number on the ID'}
            <input value={form.idNumber} onChange={set('idNumber')} autoComplete="off" disabled={!!busy}/></label>
          <label>Name on the ID<input value={form.nameOnId} onChange={set('nameOnId')} autoComplete="off" disabled={!!busy}/></label>
          <label>Date of birth on the ID<input type="date" value={form.dobOnId} onChange={set('dobOnId')} disabled={!!busy}/></label>
          <span className="mod-note">The ID number stops the same person getting a second checked account.</span>
          <div className="mod-action-row"><button className="button button-blue" disabled={!!busy}>{busy === 'approve' ? 'Approving…' : 'Approve'}</button></div>
        </form>
        <form className="mod-form" onSubmit={reject}>
          <strong>Reject</strong>
          <label>Reason
            <select value={form.reason} onChange={set('reason')} disabled={!!busy}>
              <option value="">Choose a reason</option>
              {rejectionReasons.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Note (optional)<textarea value={form.note} onChange={set('note')} maxLength={500} disabled={!!busy}/></label>
          <div className="mod-action-row"><button className="button button-secondary" disabled={!!busy}>{busy === 'reject' ? 'Rejecting…' : 'Reject'}</button></div>
        </form>
      </div>
    </section>
  );
}
